import type { SubmitHandler } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import type { InferType } from 'yup';
import type { tikTokSchema } from '../validation/tiktok.schema';
import { memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useCreateTikTokAd } from '../api/createTikTokAd';
import { AdCreationProgress, AddPackButton, CircularProgress, ConfirmationDialog, } from 'components/common';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import { JobResultTable } from './TikTokFormComponents';
import { FormNavigation, StepperPaper } from 'components/layout';
import useStepper from 'hooks/useStepper';
import ChooseCampaign from './ChooseCampaign';
import ChooseCreative from './ChooseCreative';
import ChooseAdSet from './ChooseAdSet';
import AdSettings from './AdSettings';
import { useEnqueueSnackbar } from 'hooks';
import AppAccount from 'features/tiktok/components/AppAccount';
import useCreateTikTokAdParams from '../hooks/useCreateTikTokAdParams';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { useGlobalSettings } from 'providers';

export type TikTokFormValues = InferType<typeof tikTokSchema>;

const TikTokForm = memo(() => {
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const { selectedApp } = useGlobalSettings();

  const {
    watch,
    trigger,
    reset: resetForm,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useFormContext<TikTokFormValues>();
  const selectedFormApp = watch('app');

  const initialSteps = useMemo(
    () => [
      { title: 'Ad Account', component: AppAccount },
      { title: 'Creatives', component: ChooseCreative },
      { title: 'Campaign', component: ChooseCampaign },
      { title: 'Ad sets', component: ChooseAdSet },
      { title: 'Ad settings', component: AdSettings },
    ],
    []
  );

  const { currentStep, resetSteps, prevStep, nextStep, StepComponent } =
    useStepper({ initialSteps, defaultStep: 0 });

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  const createTikTokAd = useCreateTikTokAd({
    config: {
      onSuccess: (r) => {
        datadogActions.trackDatadogAction(DATADOG_EVENT.createTikTokAdSuccess, {
          jobId: r.job_id as string,
        });
        runJubStatusCheck(r.job_id as string);
      },
      onError: (err) => {
        datadogActions.trackDatadogError(err, { source: 'create_tiktok_ad_error' });

        if (err?.cause) {
          onShowErrorAlert(
            (err.cause as { details?: string })?.details ?? 'Error occur',
            { delayAlert: 3500 },
          );
          console.error(err.cause);
          return;
        }

        onShowErrorAlert('Internal Error');
      },
    },
  });

  const { params: createTikTokAdParams } = useCreateTikTokAdParams();

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  useEffect(() => {
    if (
      !selectedApp ||
      String(selectedFormApp ?? '') === String(selectedApp)
    ) {
      return;
    }

    resetForm({
      currentStep: 0,
      app: selectedApp,
      adType: 'SINGLE_VIDEO',
      accountId: '',
      selectedAdGroups: [],
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCreatives: [],
      packsWithCreatives: [
        {
          tag: '',
          creatives: [],
          filterCreativeByName: [],
        },
      ],
      activePacksWithCreatives: 0,
      filterCreativeByName: '',
      filterCampaignsByName: [],
      filterAdSetsByName: [],
      $adSetsData: [],
    });
    resetSteps();
    clearJobStatus();
  }, [
    clearJobStatus,
    resetForm,
    resetSteps,
    selectedApp,
    selectedFormApp,
  ]);

  useEffect(() => {
    const stepTitle = initialSteps[currentStep]?.title;
    if (!stepTitle) {
      return;
    }

    datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormStepChange, {
      stepTitle,
      stepIndex: currentStep,
    });
  }, [currentStep, initialSteps]);

  const onSubmit = useCallback<SubmitHandler<TikTokFormValues>>(() => {
    try {
      datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormSubmitAttempt, {
        app: createTikTokAdParams.app,
        adAccountId: createTikTokAdParams.ad_account_id,
        campaignTemplatesCount: createTikTokAdParams.campaign_templates?.length ?? 0,
        creativePacksCount: createTikTokAdParams.creative_packs?.length ?? 0,
        adSettingsCount: createTikTokAdParams.ad_settings?.length ?? 0,
      });
      createTikTokAd.mutate(createTikTokAdParams);
    } catch (error) {
      datadogActions.trackDatadogError(error as Error, {
        source: 'tiktok_form_submit_try_catch',
      });
      onShowErrorAlert('Error during TikTok ad creation', {
        delayAlert: 3500,
      });
      console.error('Error during TikTok ad creation:', error);
    }
  }, [createTikTokAd, createTikTokAdParams, onShowErrorAlert]);

  const handleNextStep = useCallback(() => {
    void trigger();
    if (!isValid) return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    isValid,
  ]);

  const handlePrevStep = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const handleRestart = useCallback(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.tikTokFormRestart);
    resetForm();
    resetSteps();
    clearJobStatus();
  }, [resetForm, resetSteps, clearJobStatus]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/tiktok', '_blank', 'noopener');
  }, []);

  const isDisabledNext =
    !isValid || createTikTokAd.isPending || isJobStatusFinished;

  return (
    <StepperPaper
      title={'TikTok Ads Automation'}
      steps={initialSteps}
      activeStep={currentStep}
    >
      <>
        {isJobStatusChecking ? (
          <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
        ) : (
          <Suspense fallback={<CircularProgress />}>
            {isJobStatusFinished && jobStatusResponse ? (
              <JobResultTable rows={jobStatusResponse.result} />
            ) : (
              <StepComponent />
            )}
          </Suspense>
        )}
        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep && !isJobStatusFinished}
          onBack={handlePrevStep}
          isDisabledBack={
            !currentStep || createTikTokAd.isPending || isJobStatusFinished || !!isJobStatusChecking
          }
          onNext={handleNextStep}
          isDisabledNext={isDisabledNext ?? !!isJobStatusChecking}
          titleNext={
            currentStep === initialSteps.length - 1 ? 'Create Ad' : 'Next'
          }
          childrenOnTheRightSide={
            <>{currentStep === 1 ? <AddPackButton /> : null}</>
          }
        />
      </>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog
          isUnblock={createTikTokAd.isSuccess && isJobStatusFinished}
        />
      )}
    </StepperPaper>
  );
});

TikTokForm.displayName = 'TikTokForm';

export default TikTokForm;
